import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, List, X, ExternalLink, Pencil, Ban, RotateCcw, Trash2, AlertTriangle, Film, Type } from 'lucide-react';
import { toast } from 'react-toastify';
import { redes, errorMsg } from './redes.api';
import useBreakpoint from '../hooks/useBreakpoint';
import { RED, ESTADO, TZ, diaAR, hoyAR, cuando } from './util';
import { formatTime24 } from '../utils/formatTime24';

const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const horaAR = (iso) => formatTime24(iso, { timeZone: TZ });

function Iconos({ p, size = 12 }) {
  const rs = Array.from(new Set(p.destinos.map((d) => d.red)));
  return <span className="inline-flex -space-x-1">{rs.map((r) => { const I = RED[r].Icono; return <span key={r} className={`w-4 h-4 rounded-full flex items-center justify-center text-white ring-1 ring-white ${RED[r].fondo}`}><I size={size - 3} /></span>; })}</span>;
}

function Portada({ p, className }) {
  const m = p.media?.[0];
  if (!m) return <div className={`bg-gray-100 flex items-center justify-center text-gray-400 ${className}`}><Type size={16} /></div>;
  if (m.tipo === 'video') return <div className={`relative bg-black ${className}`}><video src={m.url} muted playsInline preload="metadata" className="w-full h-full object-cover" /><Film size={12} className="absolute bottom-1 right-1 text-white" /></div>;
  return <img src={m.url} alt="" loading="lazy" className={`object-cover bg-gray-100 ${className}`} />;
}

function Detalle({ p, onClose, onEditar, onCambio }) {
  const accion = async (fn, ok, confirmar) => {
    if (confirmar && !window.confirm(confirmar)) return;
    try { await fn(); toast.success(ok); onCambio(); onClose(); } catch (e) { toast.error(errorMsg(e)); }
  };
  const E = ESTADO[p.estado] || ESTADO.programada;
  const hayError = p.destinos.some((d) => d.estado === 'error');
  const cancelable = p.destinos.some((d) => d.estado === 'programada' || d.estado === 'error');
  const borrable = !p.destinos.some((d) => d.estado === 'publicada' || d.estado === 'publicando');
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true" aria-label="Detalle de la publicación">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${E.c}`}>{E.t}</span><span className="text-sm text-gray-600">{cuando(p.fechaAt)}</span></div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Cerrar"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-4">
          {p.media?.length > 0 && <div className="flex gap-2 overflow-x-auto">{p.media.map((m) => <Portada key={m.url} p={{ media: [m] }} className="w-24 h-24 rounded-lg shrink-0" />)}</div>}
          <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{p.texto || <span className="text-gray-400">(sin texto)</span>}{p.hashtags?.length ? `\n\n${p.hashtags.map((h) => '#' + h).join(' ')}` : ''}</p>
          <div className="space-y-2">
            {p.destinos.map((d) => {
              const R = RED[d.red];
              const De = ESTADO[d.estado] || ESTADO.programada;
              return (
                <div key={d.cuentaId} className="rounded-xl border border-gray-100 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-white ${R.fondo}`}><R.Icono size={14} /></span>
                    <span className="text-sm font-semibold text-gray-800 flex-1 truncate">{d.nombre}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${De.c}`}>{De.t}</span>
                  </div>
                  {d.estado === 'publicada' && (d.link ? <a href={d.link} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-blue-700">Ver en {R.nombre} <ExternalLink size={12} /></a> : <div className="mt-1.5 text-xs text-gray-500">Publicada {d.publicadaAt ? cuando(d.publicadaAt) : ''}{p.modo === 'simulado' ? ' (simulada)' : ''}</div>)}
                  {d.error && d.estado !== 'publicada' && <div className={`mt-1.5 text-xs rounded-lg px-2.5 py-1.5 flex gap-1.5 ${d.estado === 'error' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`}><AlertTriangle size={13} className="shrink-0 mt-0.5" /><span>{d.error}{d.estado === 'programada' && d.proximoIntentoAt ? ` Próximo intento: ${horaAR(d.proximoIntentoAt)}.` : ''}</span></div>}
                  {d.errorTecnico && <div className="mt-1 text-[10px] text-gray-400 break-all">Soporte: {d.errorTecnico}</div>}
                </div>
              );
            })}
          </div>
        </div>
        <div className="px-4 pb-4 flex flex-wrap gap-2">
          {p.editable && p.estado !== 'error' && <button type="button" onClick={() => { onClose(); onEditar(p); }} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700"><Pencil size={14} />Editar</button>}
          {hayError && <button type="button" onClick={() => accion(() => redes.reintentar(p.id), 'Reintentando…')} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700"><RotateCcw size={14} />Reintentar</button>}
          {cancelable && <button type="button" onClick={() => accion(() => redes.cancelar(p.id), 'Cancelada', '¿Cancelar esta publicación? No va a salir.')} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"><Ban size={14} />Cancelar</button>}
          {borrable && <button type="button" onClick={() => accion(() => redes.borrar(p.id), 'Borrada', '¿Borrarla del calendario?')} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 ml-auto"><Trash2 size={14} />Borrar</button>}
        </div>
      </div>
    </div>
  );
}

export default function Calendario({ lista, onEditar, onCambio, onCrearEn }) {
  const hoy = hoyAR();
  const { isMobile } = useBreakpoint();
  const [vista, setVista] = useState(() => (isMobile ? 'lista' : 'mes'));
  const [mes, setMes] = useState(hoy.slice(0, 7));
  const [filtro, setFiltro] = useState('todas');
  const [abierta, setAbierta] = useState(null);

  const visibles = useMemo(() => (lista || []).filter((p) => filtro === 'todas' || (filtro === 'programadas' ? ['programada', 'publicando'].includes(p.estado) : filtro === 'publicadas' ? ['publicada', 'parcial'].includes(p.estado) : ['error', 'parcial'].includes(p.estado))), [lista, filtro]);
  const porDia = useMemo(() => visibles.reduce((a, p) => { (a[diaAR(p.fechaAt)] = a[diaAR(p.fechaAt)] || []).push(p); return a; }, {}), [visibles]);

  const [y, m] = mes.split('-').map(Number);
  const primero = new Date(Date.UTC(y, m - 1, 1));
  const offset = (primero.getUTCDay() + 6) % 7;
  const diasMes = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const celdas = Array.from({ length: Math.ceil((offset + diasMes) / 7) * 7 }, (_, i) => {
    const n = i - offset + 1;
    return n >= 1 && n <= diasMes ? `${mes}-${String(n).padStart(2, '0')}` : null;
  });
  const moverMes = (d) => { const x = new Date(Date.UTC(y, m - 1 + d, 1)); setMes(x.toISOString().slice(0, 7)); };
  const errores = (lista || []).filter((p) => p.estado === 'error' || p.estado === 'parcial').length;

  const proximas = visibles.filter((p) => ['programada', 'publicando'].includes(p.estado)).sort((a, b) => a.fechaAt.localeCompare(b.fechaAt));
  const pasadas = visibles.filter((p) => !['programada', 'publicando'].includes(p.estado)).sort((a, b) => b.fechaAt.localeCompare(a.fechaAt));

  const Fila = ({ p }) => {
    const E = ESTADO[p.estado] || ESTADO.programada;
    return (
      <button type="button" onClick={() => setAbierta(p)} className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-gray-50 rounded-lg px-2">
        <Portada p={p} className="w-14 h-14 rounded-lg shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-gray-500"><Iconos p={p} /><span>{cuando(p.fechaAt)}</span></div>
          <div className="text-sm text-gray-800 truncate mt-0.5">{p.texto || '(sin texto)'}</div>
        </div>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${E.c}`}>{E.t}</span>
      </button>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-gray-900 mr-auto">Calendario de publicaciones</h3>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          {[['mes', 'Mes', CalendarDays], ['lista', 'Lista', List]].map(([id, t, I]) => <button key={id} type="button" onClick={() => setVista(id)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold ${vista === id ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}><I size={13} />{t}</button>)}
        </div>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {[['todas', 'Todas'], ['programadas', 'Programadas'], ['publicadas', 'Publicadas'], ['errores', `Con error${errores ? ` (${errores})` : ''}`]].map(([id, t]) => <button key={id} type="button" onClick={() => setFiltro(id)} className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap border ${filtro === id ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>{t}</button>)}
      </div>

      {!lista ? <div className="h-40 animate-pulse bg-gray-50 rounded-xl" /> : vista === 'mes' ? (
        <div>
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => moverMes(-1)} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Mes anterior"><ChevronLeft size={18} /></button>
            <div className="text-sm font-semibold text-gray-800">{MESES[m - 1]} {y}</div>
            <button type="button" onClick={() => moverMes(1)} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Mes siguiente"><ChevronRight size={18} /></button>
          </div>
          <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-xl overflow-hidden text-xs">
            {DIAS.map((d) => <div key={d} className="bg-gray-50 text-center py-1.5 font-semibold text-gray-500">{d}</div>)}
            {celdas.map((f, i) => {
              const ps = (f && porDia[f]) || [];
              const pasado = f && f < hoy;
              return (
                <div key={i} className={`bg-white min-h-[92px] p-1 flex flex-col gap-1 ${!f ? 'bg-gray-50' : ''}`}>
                  {f && (
                    <div className="flex items-center justify-between">
                      <span className={`text-[11px] w-6 h-6 flex items-center justify-center rounded-full ${f === hoy ? 'bg-blue-600 text-white font-bold' : pasado ? 'text-gray-400' : 'text-gray-700'}`}>{Number(f.slice(8))}</span>
                      {!pasado && onCrearEn && <button type="button" onClick={() => onCrearEn(f)} className="text-gray-300 hover:text-blue-600 text-base leading-none px-1" aria-label={`Programar el ${f}`}>+</button>}
                    </div>
                  )}
                  {ps.slice(0, 3).map((p) => {
                    const E = ESTADO[p.estado] || ESTADO.programada;
                    return (
                      <button key={p.id} type="button" onClick={() => setAbierta(p)} className={`w-full flex items-center gap-1 rounded-md px-1 py-0.5 text-left ${E.c}`} title={p.texto}>
                        <Iconos p={p} size={12} />
                        <span className="truncate text-[10px] font-semibold">{horaAR(p.fechaAt)}</span>
                      </button>
                    );
                  })}
                  {ps.length > 3 && <span className="text-[10px] text-gray-500 px-1">+{ps.length - 3} más</span>}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Próximas</div>
            {proximas.length ? <div className="divide-y divide-gray-100">{proximas.map((p) => <Fila key={p.id} p={p} />)}</div> : <p className="text-sm text-gray-500 py-2">No hay nada programado.</p>}
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Historial</div>
            {pasadas.length ? <div className="divide-y divide-gray-100">{pasadas.map((p) => <Fila key={p.id} p={p} />)}</div> : <p className="text-sm text-gray-500 py-2">Todavía no salió nada.</p>}
          </div>
        </div>
      )}
      {abierta && <Detalle p={(lista || []).find((x) => x.id === abierta.id) || abierta} onClose={() => setAbierta(null)} onEditar={onEditar} onCambio={onCambio} />}
    </div>
  );
}
